/**
 * useStageNavigation — the Stage 1→5 forward/back navigation seam.
 *
 * TS-060 T5 slice 5 (DEC-COUNCIL-015): `canGoNext`,
 * `openStage5FromCurrentScope`, `handleNext` and `handleBack` moved VERBATIM
 * from App.jsx (v5.46.76 base 72848712 lines 10602-10649, 10651-10702,
 * 10785-10963 and 10984-11028; post-slice-4 tree lines 8645-8692, 8694-8745,
 * 8828-9006 and 9027-9071) behind a deps object. The hook owns NO navigation
 * state — every state value, setter, ref, and helper threads in from App.jsx
 * per the useQuoteEditor pattern (state stays in App; a hook that owns a
 * second copy of pipeline state is a defect). Module-level pure imports
 * (stage1SummaryContinuation + PROJECT_WORKFLOW_MODES,
 * buildStage5DraftQuoteFromScope, isPricingWorktableEnabled) are imported
 * here directly, matching the T3 leaf-component convention. The
 * celebration-effect inline predicate mirror (which CALLS the returned
 * canGoNext), the quote-editor aliases
 * (`openFinalQuoteEditor`/`closeQuoteEditor`) and the QuoteMakerNavBar
 * onSelectStage inline handler ALL STAY in App.jsx.
 *
 * Identity contract: all four returned closures are deliberately re-created
 * every render (NO useCallback/useMemo) exactly like the original per-render
 * closures — the celebration effect, popstate handler, JSX props and Stage
 * 1/3/4 page props call whatever the current render exposed.
 *
 * Hoisting note: `openStage5FromCurrentScope` was a hoisted `function`
 * declaration in App.jsx; here it is a hook-scope const, so the binding
 * exists only from the App.jsx instantiation line onward. Audited every
 * App.jsx reference: the two textually-above sites (the popstate handler,
 * App.jsx:1502/1526) live inside an event-handler body that runs strictly
 * after render completes, so the destructured const is assigned by call
 * time; every other reference sits below the instantiation. handleNext's
 * own `openStage5FromCurrentScope({ persistStage: true })` call resolves to
 * this hook-scope const with identical same-render-closure semantics.
 *
 * TDZ note: two App bindings the bodies call are declared BELOW the App.jsx
 * instantiation point — `openFinalQuoteEditor` and `closeQuoteEditor` (the
 * DEC-COUNCIL-013 useQuoteEditor aliases, declared after handleBack in the
 * original). They thread as lazy wrappers: building an arrow at deps-build
 * time does not evaluate the binding; `openFinalQuoteEditor` is only CALLED
 * as openStage5FromCurrentScope's terminal delegate and `closeQuoteEditor`
 * only inside handleBack's stage-5 branch — both handler bodies run after
 * render completes, so the wrappers are runtime-equivalent to the original
 * lazy closure reads.
 *
 * Receiver preservation: these bodies make NO service-object method calls;
 * the only property-access callees are platform globals/built-ins
 * (`localStorage.getItem`, `Object.keys`, `Array.isArray`, `Array.from`,
 * `console.warn`, `prev.has`, promise `.catch`) — all kept verbatim as
 * property-access calls, no detached aliases introduced.
 */

import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { stage1SummaryContinuation, PROJECT_WORKFLOW_MODES } from '../services/project-workflow';
import { hasMeaningfulProjectInput, summaryViewAction } from '../utils/meaningful-input';
import { buildStage5DraftQuoteFromScope } from '../services/stage5-draft-quote';
import { getInstanceOwnerCached } from '../services/tenant-resolver';
import { mergeIncorporatedBoundaryExclusions } from '../utils/incorporated-boundary-exclusions';
import { isPricingWorktableEnabled } from '../utils/feature-flags';
import { type ProjectRunContext } from '../utils/project-run-context';
import { isBrainProject, quoteBrainRefusalMessage, quoteBrainStage1Route } from '../utils/quote-brain-project';

/** Minimal structural view of App's formData — only fields this seam touches. */
interface StageNavFormData {
    notes?: string;
    [key: string]: unknown;
}

/** WMS envelope as stored on the project — only the readiness flag is read. */
interface ProjectWmsLike {
    structured?: unknown;
    [key: string]: unknown;
}

interface ToastPayload {
    message: string;
    type: string;
    /** Keep a durability warning visible until the operator dismisses it. */
    sticky?: boolean;
}

interface ShowConfirmOptions {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string | null;
    confirmColor?: string;
    icon?: string;
    zIndex?: number;
    onConfirm?: () => unknown;
    onCancel?: () => void;
    onDismiss?: () => void;
    defaultFocus?: 'cancel' | 'confirm';
}

/** Options bag for openStage5FromCurrentScope (annotation added at the move). */
interface OpenStage5FromCurrentScopeOptions {
    /** Explicit quoteData fallback; the live ref remains authoritative. */
    sourceQuoteData?: unknown;
    /** false = open the editor without the Stage 4→5 transition side-effects. */
    transitionToStage5?: boolean;
    /** Request persistence of the same package that is painted immediately. */
    persistStage?: boolean;
    /** Opt into the Stage 5 open/durability receipt. */
    returnOutcome?: boolean;
}

interface Stage45PersistenceReceipt {
    projectId: string | null;
    loadGeneration: number | null;
    scopeFingerprint: string;
    quoteFingerprint: string;
}

export interface UseStageNavigationDeps {
    // ── State values (fresh each render, same as the original closures) ──
    stage: number;
    isStage1Processing: boolean;
    isSummarizing: boolean;
    projectSummary: unknown;
    summaryError: unknown;
    formData: StageNavFormData;
    documents: unknown[];
    photos: unknown[];
    /** Retained videos (any outcome). With notes, documents and photos they are Stage 1's evidence (C03). Optional so a headless caller reads none. */
    videos?: unknown[];
    /** Transcripts of those videos, the one lane the Summary's admission counts that the arrays above do not. Optional: absent reads none. */
    videoTranscripts?: unknown[];
    analysis: unknown;
    projectWMS: ProjectWmsLike | null;
    scope: unknown[];
    quoteData: unknown;
    quoteNote: string;
    scopeGstInclusive: boolean;
    quoteTitle: string;
    selectedHeroImage: unknown;
    completedStages: Set<number>;
    summaryNeedsRefresh: boolean;
    /**
     * The summary on screen is out of date, and a new one made from the same
     * saved copy would be out of date again (App's summaryRerunHeld). NEXT then
     * goes on with it as it is instead of paying for the same answer again.
     */
    summaryRerunHeld?: boolean;
    projectWorkflowMode: string;
    /** App's workflow-mode setter: every later save writes App's mode, so a job written by hand is set there too. */
    setProjectWorkflowMode?: (mode: string) => void;
    pricingWorktableActive: boolean;
    // ── Setters (App-owned) — setStageWithTransition is App's stage setter
    // + transition wrapper. ──
    /**
     * One-shot request for a Stage 1 Job cockpit tab (SPEC 2026-10-21 §14.2).
     * Back to Stage 1 shows the Summary tab. Optional so headless callers are unchanged.
     */
    requestCockpitTab?: (tab: 'summary' | 'files') => void;
    /**
     * B5: run the summary as the operator's own run, then press NEXT again once
     * it has settled with a fresh summary. Optional: a headless caller falls
     * back to running the summary alone.
     */
    runProjectSummaryThenNext?: () => Promise<unknown>;
    setCompletedStages: Dispatch<SetStateAction<Set<number>>>;
    setStageWithTransition: (stage: number) => void;
    setToast: (toast: ToastPayload) => void;
    setPricingWorktableActive: (value: boolean) => void;
    setImportPhase: (phase: string) => void;
    // ── Live-mirror refs (App-owned) ──
    scopeRef: MutableRefObject<unknown[]>;
    quoteDataRef: MutableRefObject<unknown>;
    quoteNoteRef: MutableRefObject<string | null>;
    batchActiveRef: MutableRefObject<boolean>;
    stageRef: MutableRefObject<number>;
    currentPageRef: MutableRefObject<string>;
    currentProjectIdRef: MutableRefObject<string | null>;
    projectLoadGenerationRef: MutableRefObject<number>;
    projectAbortRef: MutableRefObject<AbortController>;
    stage45PersistenceReceiptRef?: MutableRefObject<Stage45PersistenceReceipt | null>;
    // ── App-owned helpers ──
    showConfirm: (options: ShowConfirmOptions) => void;
    saveCurrentProject: (
        overrides?: Record<string, unknown>,
        options?: { runContext?: ProjectRunContext },
    ) => Promise<unknown>;
    runProjectSummary: (options?: Record<string, unknown>) => unknown;
    /** Synchronous current-project Final Quote singleflight ownership probe. */
    isQuoteGenerationInFlight: () => boolean;
    /**
     * R4: a job written by hand gets its HBCF on its first quote, the Stage 4
     * to 5 draft. Called before the draft is built; returns a promise only
     * while it adds HBCF (App's applyFirstRunHbcfBeforeStage5). Optional so
     * headless callers are unchanged.
     */
    applyFirstRunHbcf?: () => Promise<unknown> | null | undefined;
    // ── Late-declared App deps (TDZ) — lazy wrappers, see header note ──
    openFinalQuoteEditor: (source: unknown, opts: {
        transitionToStage5?: boolean;
        persistStage?: boolean;
        viewOnlyNavigation?: boolean;
        returnOutcome?: boolean;
        persistenceReceipt?: Stage45PersistenceReceipt | null;
    }) => Promise<unknown>;
    closeQuoteEditor: () => void;
}

export function useStageNavigation(deps: UseStageNavigationDeps) {
    const {
        stage,
        isStage1Processing,
        isSummarizing,
        projectSummary,
        summaryError,
        formData,
        documents,
        photos,
        videos = [],
        videoTranscripts = [],
        analysis,
        projectWMS,
        scope,
        quoteData,
        quoteNote,
        scopeGstInclusive,
        quoteTitle,
        selectedHeroImage,
        completedStages,
        summaryNeedsRefresh,
        summaryRerunHeld = false,
        projectWorkflowMode,
        setProjectWorkflowMode,
        pricingWorktableActive,
        requestCockpitTab,
        runProjectSummaryThenNext,
        setCompletedStages,
        setStageWithTransition,
        setToast,
        setPricingWorktableActive,
        setImportPhase,
        scopeRef,
        quoteDataRef,
        quoteNoteRef,
        batchActiveRef,
        stageRef,
        currentPageRef,
        currentProjectIdRef,
        projectLoadGenerationRef,
        projectAbortRef,
        stage45PersistenceReceiptRef,
        showConfirm,
        saveCurrentProject,
        runProjectSummary,
        isQuoteGenerationInFlight,
        applyFirstRunHbcf,
        openFinalQuoteEditor,
        closeQuoteEditor,
    } = deps;

    const canGoNext = () => {
        switch (stage) {
            case 1:
                if (isStage1Processing) return false;
                if (isSummarizing) return false;
                // Need notes/docs/photos/videos to summarize. This is the SAME rule the Summary's own admission applies
                // (meaningful-input.ts), so the header Next and the run cannot disagree (C03): a retained video counts, visibly
                // empty notes (O-S9) do not. A successfully generated summary is enough on its own: NEXT then continues from it.
                if (projectSummary && !summaryError) return true;
                return hasMeaningfulProjectInput({
                    notes: formData.notes,
                    documentCount: documents.length,
                    photoCount: photos.length,
                    transcriptCount: videoTranscripts.length,
                    videoCount: videos.length,
                });
            // Stage 4 (Scope Builder): allow Stage 5 once there are scope
            // items to view as a quote. Was missing in the old 4-stage switch,
            // which is why the NEXT button stayed disabled on Stage 4 after
            // the 5-stage refactor. Fix 2026-04-19.
            //
            // 2026-08-25: readiness findings are visible inside Stage 5 and
            // enforced by the relevant output action. They must not hide that
            // peer view or silently grey its navigation control.
            case 4:
                // Stage 4 and Stage 5 are peer views over the same live scope.
                // A deliberate Stage 4 title/method edit can make the earlier
                // WMS projection or costing receipt stale; that finding belongs
                // in Stage 5's visible readiness/export gates, not in a greyed
                // navigation button. Only an empty scope waits.
                return scope.length > 0;
            // Stage 5 (Final Quote) is the terminal stage — no NEXT button.
            // The BACK button is handled by handleBack which routes Stage 5 → 4.
            // Made explicit (was falling through to `default: false` before
            // v5.19.3) so future maintainers don't add a Stage 5 NEXT branch
            // expecting it to fire.
            case 5: return false;
            default: return false;
        }
    };

    const openStage5FromCurrentScope = async ({
        sourceQuoteData = null,
        transitionToStage5 = true,
        persistStage = false,
        returnOutcome = false,
    }: OpenStage5FromCurrentScopeOptions = {}) => {
        const navigationOrigin = {
            stage: stageRef.current,
            page: currentPageRef.current,
            projectId: currentProjectIdRef.current,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectAbortRef.current?.signal || null,
        };
        const navigationIntentIsCurrent = () => (
            stageRef.current === navigationOrigin.stage
            && currentPageRef.current === navigationOrigin.page
            && currentProjectIdRef.current === navigationOrigin.projectId
            && projectLoadGenerationRef.current === navigationOrigin.loadGeneration
            && projectAbortRef.current?.signal === navigationOrigin.signal
            && !navigationOrigin.signal?.aborted
        );
        if (!navigationIntentIsCurrent()) return false;
        // R4: the first run's HBCF goes into the items (and any quote) before
        // the draft is built from them, so it lands on the quote. A brain
        // project's HBCF is the server writer's, so it is never added here.
        const hbcfScope = Array.isArray(scopeRef.current) && scopeRef.current.length > 0 ? scopeRef.current : scope;
        const firstRunHbcf = isBrainProject(currentProjectIdRef.current, { scope: hbcfScope }) ? null : applyFirstRunHbcf?.();
        if (firstRunHbcf && typeof (firstRunHbcf as Promise<unknown>).then === 'function') {
            await firstRunHbcf;
            if (!navigationIntentIsCurrent()) return false;
        }
        const usableQuoteData = (candidate: unknown) => (
            candidate
            && typeof candidate === 'object'
            && !Array.isArray(candidate)
            && Object.keys(candidate).length > 0
                ? candidate
                : null
        );
        // The live quote ref wins over an explicit caller snapshot so a prior
        // manual/AI-authored quote is never overwritten by a stale argument.
        const sourceCandidate = usableQuoteData(quoteDataRef.current)
            || usableQuoteData(sourceQuoteData)
            || usableQuoteData(quoteData);
        const deferToActiveQuoteAuthor = () => {
            setToast({
                message: 'The replacement quote is still being generated from the saved Stage 4 scope. QuoteMaker will open Stage 5 after the replacement quote is saved.',
                type: 'info',
            });
            return false;
        };
        // The project singleflight lock is synchronous; unlike React busy
        // state, it already owns the run during Pricing/context preparation.
        // A complete prior Stage 5 snapshot is still useful: open that exact
        // read-only peer without merging or saving while replacement authoring
        // continues. Only a project with no quote to show must wait.
        if (isQuoteGenerationInFlight()) {
            if (!sourceCandidate) return deferToActiveQuoteAuthor();
            setToast({
                message: 'Showing the saved Stage 5 quote while replacement generation continues. Print, PDF and Email use the complete version currently visible.',
                type: 'info',
            });
            return openFinalQuoteEditor(sourceCandidate, {
                transitionToStage5,
                persistStage: false,
                ...(returnOutcome ? { returnOutcome: true } : {}),
            });
        }
        // Stage 5 is the deterministic presentation of Stage 4's saved scope
        // and pricing, not a second authoring prerequisite. Photo assignment is
        // optional background enrichment: an in-flight, partial or failed photo
        // selection must never hide the preview/PDF/CSV/email controls. Export
        // and delivery still pass through their existing pricing, evidence and
        // persistence gates inside FullQuoteEditorModal.
        //
        const liveScope = Array.isArray(scopeRef.current) && scopeRef.current.length > 0
            ? scopeRef.current
            : scope;
        // Navigation never owns paid regeneration. Any existing Stage 5 draft
        // opens as-is so the operator can see its stale/fallback/readiness state
        // and choose Regenerate explicitly. Treating an older or deterministic
        // draft as "missing" replaced it with a scope shell and dispatched a
        // second paid author merely by clicking Open final quote view.
        const existingSource = sourceCandidate;
        // W5 (Quote Brain DESIGN.md 2.12): a brain project's Stage 5 renders the
        // Quote Brain's list as written. No draft is built and no Work Method
        // boundary is merged into it.
        const brainProject = isBrainProject(currentProjectIdRef.current, { scope: liveScope });
        if (brainProject && !existingSource) {
            setToast({ message: quoteBrainRefusalMessage('building a Stage 5 draft', currentProjectIdRef.current), type: 'info' });
            return false;
        }
        const existingSourceWithCurrentBoundaries = existingSource
            ? (brainProject ? existingSource : mergeIncorporatedBoundaryExclusions(existingSource, projectWMS))
            : null;
        // Stage 4 and Stage 5 are peer views over one authoring package. The
        // scope-backed draft makes Stage 5 immediately viewable; an explicitly
        // requested Regenerate Quote action may replace its wording later. It
        // is not a completion receipt and navigation does not mark Stage 5 as
        // builder-reviewed or sent.
        const source = existingSourceWithCurrentBoundaries
            || buildStage5DraftQuoteFromScope({
                scope: liveScope,
                formData,
                projectSummary,
                quoteNote: quoteNoteRef.current || quoteNote,
                scopeGstInclusive,
                projectTitle: quoteTitle,
                selectedHeroImage,
                projectWMS,
                tenantProfile: getInstanceOwnerCached(),
            });

        if (!source && liveScope.length === 0) {
            showConfirm({
                title: 'Scope Required',
                message: 'Please add at least one scope item before opening the final quote view.',
                confirmText: 'Got it',
                cancelText: null,
                confirmColor: 'orange',
                icon: 'info',
                onConfirm: () => {},
            });
            return false;
        }

        const continueToFinalQuote = async () => {
            const outcome = await openFinalQuoteEditor(source, {
                transitionToStage5,
                // Every real transition paints the complete deterministic peer
                // immediately. prepareStage4And5/pipeline completion owns the
                // durability receipt; this click changes only the visible view.
                persistStage: persistStage || transitionToStage5,
                viewOnlyNavigation: true,
                persistenceReceipt: stage45PersistenceReceiptRef?.current || null,
                ...(returnOutcome ? { returnOutcome: true } : {}),
            });
            if (
                outcome
                && typeof outcome === 'object'
                && 'opened' in outcome
                && outcome.opened === true
                && 'persisted' in outcome
                && outcome.persisted === false
            ) {
                setToast({
                    message: 'Stage 5 is open, but this quote is not yet durably saved. Print, PDF and Email remain available; keep QuoteMaker open and press Save when storage recovers. Nothing has been sent.',
                    type: 'warning',
                    sticky: true,
                });
            }
            return outcome;
        };

        // Stage 5 contains the visible pricing/readiness warnings and the hard
        // print/download/email gates. Opening that peer view is never itself a
        // claim that pricing was reviewed or either stage is complete.
        return continueToFinalQuote();
    };

    // `useSummaryAsIs`: App's continuation after the summary NEXT asked for
    // landed out of date for evidence nobody changed meanwhile (see
    // summaryRerunHeld). A click passes its event here, which never carries it.
    const handleNext = async (options?: { useSummaryAsIs?: boolean }) => {
        if (stage === 1) {
            // ─── Stage 1 → Risk Analysis (Scope Builder on Fast-Track) ───
            // One press does the rest (SPEC 2026-10-21 B5): it makes or
            // refreshes the summary first when that is needed, then continues.
            // A QuoteMaker job (quoteBrainStage1Route): while it writes, or the banner promises the server's start, the
            // run banner is the way on and the button is not shown (a job written by hand keeps its route); once it has
            // written, the button opens its items. The same rule NavActions shows, read at the press.
            const quoteBrainRoute = quoteBrainStage1Route(currentProjectIdRef.current, { scope, wms: projectWMS, riskAnalysis: analysis });
            if (quoteBrainRoute === 'writing' && projectWorkflowMode !== PROJECT_WORKFLOW_MODES.FAST_TRACK_MANUAL_SCOPE) return;
            if (quoteBrainRoute === 'written') {
                const nextCompleted = new Set([...completedStages, 1]);
                setCompletedStages(nextCompleted);
                setStageWithTransition(4);
                return;
            }
            // The same open-or-generate decision the old Summary page made (5A / O-S9): a stale Summary with nothing left to
            // regenerate it from (the notes cleared to empty markup, no file, photo or clip) is used as it is, because
            // regenerating would only be refused. A failed Summary is remade.
            const useSummaryAsIs = summaryRerunHeld || options?.useSummaryAsIs === true;
            const summaryAction = summaryViewAction({
                hasSummary: !!projectSummary && !summaryError,
                summaryNeedsRefresh: summaryNeedsRefresh && !useSummaryAsIs,
                hasInput: hasMeaningfulProjectInput({
                    notes: formData.notes,
                    documentCount: documents.length,
                    photoCount: photos.length,
                    transcriptCount: videoTranscripts.length,
                    videoCount: videos.length,
                }),
            });
            if (summaryAction === 'generate') {
                if (typeof runProjectSummaryThenNext === 'function') await runProjectSummaryThenNext();
                else await runProjectSummary();
                return;
            }
            // On to the Scope Builder (stage1SummaryContinuation): the Risk report and the Work Method went with the old
            // pipeline (docs/old-pipeline-removal/PLAN.md). Going on by hand makes the job the builder's to write, so the
            // server never starts QuoteMaker on it (quote-brain-live/metrics.js MANUAL_WORKFLOW_MODE).
            const summaryNext = stage1SummaryContinuation(projectWorkflowMode);
            // App's own mode too: every later save writes it (use-project-persistence.ts), and a save that wrote the old
            // mode back would let the server start QuoteMaker on a job the builder is writing.
            setProjectWorkflowMode?.(PROJECT_WORKFLOW_MODES.FAST_TRACK_MANUAL_SCOPE);
            const nextCompleted = new Set([...completedStages, 1]);
            setCompletedStages(nextCompleted);
            setStageWithTransition(summaryNext.nextStage);
            await saveCurrentProject({
                currentStage: summaryNext.nextStage,
                completedStages: Array.from(nextCompleted),
                workflowMode: PROJECT_WORKFLOW_MODES.FAST_TRACK_MANUAL_SCOPE,
            }).catch(err => {
                console.warn('[handleNext Stage 1->4] save failed (continuing):', err?.message);
            });
        } else if (stage === 4) {
            // Pricing Worktable interstitial (opt-in, interactive only). On the
            // first forward NEXT from Stage 4, divert to the manual pricing
            // worktable; its own "Continue to quote" re-runs this path with the
            // worktable dismissed. Skipped in batch mode (the runner auto-fires
            // straight to the quote). Mirrors the Stage 1.5 summary diversion.
            if (isPricingWorktableEnabled() && !pricingWorktableActive && !batchActiveRef.current) {
                setPricingWorktableActive(true);
                return;
            }
            // Scope Builder -> Final Quote. Stage 5 is a second view over the
            // authored Stage 4 scope, so missing AI quoteData must not trap the
            // user on Stage 4; build a deterministic draft from the live rows.
            await openStage5FromCurrentScope({ persistStage: true });
        }
    };

    const handleBack = () => {
        // ─── Pricing Worktable → Scope Builder ───
        if (stage === 4 && pricingWorktableActive) {
            setPricingWorktableActive(false);
            return;
        }
        // Stage 1 is the first screen: there is no Back from it.
        // ─── Scope Builder (stage 4) → Stage 1 ───
        if (stage === 4) {
            // Every job goes from Stage 1 straight to Scope (the Risk report and the Work Method went with the old
            // pipeline, docs/old-pipeline-removal/PLAN.md), so Back retraces that route: Stage 1 with the Summary tab when
            // there is a summary.
            if (projectSummary) {
                requestCockpitTab?.('summary');
            } else {
                setImportPhase('form');
            }
            setStageWithTransition(1);
            return;
        }
        // ─── Final Quote (stage 5) → Scope Builder (stage 4) ───
        if (stage === 5) {
            closeQuoteEditor();
            return;
        }
    };

    return { canGoNext, openStage5FromCurrentScope, handleNext, handleBack };
}
